import { FormField, PathHop } from '@/features/application/types/forms';
import { hasPathHops } from './path-display.utils';

/**
 * Converts a PathHop array to a user-friendly string with arrows
 * Skips the anchor hop and formats locators appropriately
 */
const pathToDisplayString = (path: PathHop[]): string => {
  if (!path || path.length === 0) return '';

  return path
    .filter((hop) => hop.rel) // Skip anchor hop (no rel)
    .map((hop) => {
      let hopString = hop.rel || hop.object;
      if (hop.locator) {
        if (hop.locator.strategy === 'fieldValue') {
          const entries = Object.entries(hop.locator.params || {});
          if (entries.length > 0) {
            const [key, value] = entries[0];
            hopString += `[${key}=${value}]`;
          }
        } else if (hop.locator.strategy === 'positional') {
          hopString += `[${hop.locator.params.index || 0}]`;
        }
      }
      return hopString;
    })
    .join(' → ');
};

/**
 * Generates a human-readable summary for a field mapping
 * Shows both path context and the actual field name when available
 * Uses arrows for better visual hierarchy
 */
export const generateMappingSummary = (field: FormField): string => {
  const isMapped = !!field.fieldApiName;

  if (!isMapped) return 'Not Mapped';

  // Get the field identifier (label or API name)
  const fieldIdentifier = field.fieldLabel || field.fieldApiName || '';

  // If there's a path with hops, show path + field name with arrows
  if (hasPathHops(field.path || [])) {
    const pathString = pathToDisplayString(field.path || []);

    // Combine path and field identifier with arrow
    if (pathString && fieldIdentifier) {
      return `${pathString} → ${fieldIdentifier}`;
    } else if (pathString) {
      return pathString;
    } else if (fieldIdentifier) {
      return fieldIdentifier;
    }
  }

  // Fallback to field identifier or placeholder
  return fieldIdentifier || '...';
};
